<template>
  <AppBanner>Plan Overview</AppBanner>

  <AppContentLoader
    v-if="loading"
    text="Loading Plan..."
  />

  <span
    v-else-if="!plan"
    class="text-center mt-5 block"
  >
    Failed to load plan
  </span>

  <div
    v-else
    class="flex items-start container-padding my-8"
  >
    <PlanOverviewCoverPage :plan="plan" />

    <PlanOverviewArchivedCard v-if="authStore?.currentMunicipality?.archived" />

    <div
      v-else
      class="bg-white w-[420px] border border-gray-300 ml-5 flex flex-col [&_p]:text-black"
    >
      <PlanOverviewRoleInfo :workflow-step="workflowStep" />

      <PlanOverviewWorkflowActions
        :class="{ 'order-last border-t border-chrome-light !border-b-0': workflowStep === PlanWorkflowStep.PublishedAsFinal }"
        :workflow-step="workflowStep"
        :plan-pdfs="planPdfs"
        @notify-admin-to-submit="modals.notifyAdmin.open"
        @notify-admin-to-approve="modals.notifyAdmin.open"
        @submit-for-first-mec="modals.submitForFirstMec.open"
        @submit-for-final-mec="modals.submitForFinalMec.open"
        @submit-for-approval="modals.notifyAdmin.open"
        @approve-draft-for-council="modals.approveDraftForCouncil.open"
        @approve="modals.approveDraft.open"
        @reject-draft="modals.reject.open"
        @approve-final-for-council="modals.approveFinalForCouncil.open"
        @approve-final-and-publish="modals.approveFinalAndPublish.open"
        @reject-final="modals.reject.open"
        @reopen-for-editing="modals.reopenForEditing.open"
        @download-draft="downloadForStep(PlanWorkflowStep.WorkInProgress3)"
        @download-final="downloadForStep(PlanWorkflowStep.PublishedAsFinal)"
        @regenerate="regeneratePdf"
      />

      <PlanOverviewWorkflow
        :workflow-step="workflowStep"
        :plan-pdfs="planPdfs"
        :pdfs-loading="loading"
        @download="downloadForStep"
        @regenerate="regeneratePdf"
      />
    </div>
  </div>

  <WorkflowActionModal
    v-if="modals.notifyAdmin.show && isMunicipalAdmin"
    title="Move to approval"
    :processing="modals.notifyAdmin.processing"
    @confirm="modals.notifyAdmin.confirm"
    @close="modals.notifyAdmin.show = false"
  >
    <strong>Once submitted, this document cannot be edited or withdrawn.</strong><br>Please review all content carefully before proceeding.
  </WorkflowActionModal>

  <NotifyMunicipalAdminModal
    v-if="modals.notifyAdmin.show && !isMunicipalAdmin"
    :workflow-description="notifyAdminDescription"
    :processing="modals.notifyAdmin.processing"
    @confirm="modals.notifyAdmin.confirm"
    @close="modals.notifyAdmin.show = false"
  />

  <WorkflowActionModal
    v-if="modals.approveDraftForCouncil.show"
    title="Confirm submission for Council noting"
    :processing="modals.approveDraftForCouncil.processing"
    @confirm="modals.approveDraftForCouncil.confirm"
    @close="modals.approveDraftForCouncil.show = false"
  >
    Once submission has been confirmed, the document will transition to the MEC review phase.
    Please verify that the content is final before continuing.
  </WorkflowActionModal>

  <WorkflowActionModal
    v-if="modals.submitForFirstMec.show"
    title="Confirm submission for MEC Review"
    :processing="modals.submitForFirstMec.processing"
    @confirm="modals.submitForFirstMec.confirm"
    @close="modals.submitForFirstMec.show = false"
  >
    Once submission has been confirmed, the document will transition to the subsequent
    'Work in Progress' phase. Please verify that the content is final before continuing.
  </WorkflowActionModal>

  <WorkflowActionModal
    v-if="modals.submitForFinalMec.show"
    title="Confirm submission for MEC Review"
    :processing="modals.submitForFinalMec.processing"
    @confirm="modals.submitForFinalMec.confirm"
    @close="modals.submitForFinalMec.show = false"
  >
    Once submission has been confirmed the document will move to the publishing phase.
  </WorkflowActionModal>

  <WorkflowActionModal
    v-if="modals.approveDraft.show"
    title="Confirm draft approval and publication"
    :processing="modals.approveDraft.processing"
    :confirm-text="isPlanIncomplete ? 'Submit anyway' : 'Submit'"
    @confirm="modals.approveDraft.confirm"
    @close="modals.approveDraft.show = false"
  >
    <PlanOverviewIncompletePlanWarning :plan="plan" />

    Once approval has been confirmed, the draft will be published and the document
    <strong>cannot be published as draft again.</strong> It will transition to the next Work in Progress phase for
    refinement. Please verify that the content is final before continuing.
  </WorkflowActionModal>

  <WorkflowActionModal
    v-if="modals.reject.show"
    :title="rejectModalTitle"
    :processing="modals.reject.processing"
    confirm-text="Reject"
    confirm-class="btn-danger"
    @confirm="modals.reject.confirm"
    @close="modals.reject.show = false"
  >
    If rejected, the document will revert to the <strong>previous Work in Progress</strong> phase for
    further editing.
  </WorkflowActionModal>

  <WorkflowActionModal
    v-if="modals.approveFinalForCouncil.show"
    title="Confirm submission for Council approval"
    :processing="modals.approveFinalForCouncil.processing"
    @confirm="modals.approveFinalForCouncil.confirm"
    @close="modals.approveFinalForCouncil.show = false"
  >
    Once submission has been confirmed, the document will transition to the MEC submission phase.
    Please verify that the content is final before continuing.
  </WorkflowActionModal>

  <WorkflowActionModal
    v-if="modals.approveFinalAndPublish.show"
    title="Confirm final document submission"
    :processing="modals.approveFinalAndPublish.processing"
    :confirm-text="isPlanIncomplete ? 'Submit anyway' : 'Submit'"
    @confirm="modals.approveFinalAndPublish.confirm"
    @close="modals.approveFinalAndPublish.show = false"
  >
    <PlanOverviewIncompletePlanWarning :plan="plan" />

    Once approval has been confirmed, the document will be published as the final version. Please verify that the content is final before continuing.
  </WorkflowActionModal>

  <WorkflowActionModal
    v-if="modals.reopenForEditing.show"
    title="Re-open for editing prior to submission?"
    :processing="modals.reopenForEditing.processing"
    confirm-text="Re-open"
    @confirm="modals.reopenForEditing.confirm"
    @close="modals.reopenForEditing.show = false"
  >
    If re-opened, the document will revert to the <strong>previous Work in Progress</strong> phase for further editing.
  </WorkflowActionModal>

  <DevTools v-model="workflowStep" />
</template>

<script setup>
import { HttpStatusCode } from 'axios';
import { computed, ref } from 'vue';

import { AppContentLoader, DevTools } from '@/components/app';
import { AppBanner } from '@/components/banners';

import {
  PlanOverviewWorkflowActions,
  PlanOverviewArchivedCard,
  PlanOverviewCoverPage,
  PlanOverviewIncompletePlanWarning,
  PlanOverviewRoleInfo,
  PlanOverviewWorkflow,
} from '@/components/plan/overview';

import { NotifyMunicipalAdminModal, WorkflowActionModal } from '@/components/plan/overview/modals';

import { useHttp, usePlanPdf, useWorkflowModals } from '@/composables';
import { useAuthStore } from '@/store';
import { PlanWorkflowStep, Role } from '@/const';

const authStore = useAuthStore();

const plan = ref(null);
const loading = ref(true);
const workflowStep = ref(null);

const currentMunicipality = computed(() => authStore.currentMunicipality);

const isMunicipalAdmin = computed(() => authStore.user?.roles?.includes(Role.MunicipalAdministrator));

const isPlanIncomplete = computed(() => !!(plan.value?.missingRequiredAnnexures?.length || plan.value?.blankSections?.length));

const { planPdfs, setPlanPdfs, downloadForStep, regenerate } = usePlanPdf(currentMunicipality.value.id);

const loadPlan = async () => {
  const response = await useHttp().get(`/municipalities/${currentMunicipality.value.id}/plan`);

  if (response.statusCode === HttpStatusCode.Ok) {
    plan.value = response.data;
    workflowStep.value = response.data.workflowStep ?? PlanWorkflowStep.WorkInProgress1;
    setPlanPdfs(response.data.transitions ?? []);
  }
  loading.value = false;
};

loadPlan();

// On success the snapshot returns to pending, so reload to reflect it.
const regeneratePdf = async () => {
  if (await regenerate()) await loadPlan();
};

const { modals, rejectModalTitle, notifyAdminDescription } = useWorkflowModals(workflowStep, loadPlan, currentMunicipality.value.id);
</script>
